import * as React from "react";
import { PT_ProtectiveFunction } from "../models/PT_ProtectiveFunction";
import { DM_PT_DataSheet } from "../models/DM_PT_DataSheet";
import { DeviceData } from "../models/DeviceData";
import { DM_DataSheet } from "../models/DM_DataSheet";
import { SilModal } from "./SilModal";
import { PM_ProtectiveFunction } from "../models/PM_ProtectiveFunction";
import { MasterPoolMismatchItem } from "../models/MasterPoolMismatchItem";
import { isMooNVotingType, parseMooNVoting, IMooN, parseVoting, formatNumberNotation } from "./../helpers";
import { PtType } from "../models/PtType";
import { IndividualTagsModal } from "./IndividualTagsModal";
import { lockedVotingDevices } from "../helpers";

interface Props {
  device?: DeviceData;
  closeModal: () => void;
  onDeviceUpdateClick?: (
    ptProtectiveFunction: PT_ProtectiveFunction,
    pmProtectiveFuntion: PM_ProtectiveFunction,
  ) => Promise<void>;
  onDeviceSheetRestoreClick?: (
    ptProtectiveFunctionId: number
  ) => Promise<void>;
  dataSheetInfo?: DM_PT_DataSheet;
  dm_dataSheet?: DM_DataSheet;
  masterPoolMismatchItems?: MasterPoolMismatchItem[];
  readOnly: boolean;
}

interface State {
  isAllowSubmit: boolean;
  shownModal?: | "individualTagsModal";
  formValidationFields: {
    diagnosticInterval: boolean;
    overhaulInterval: boolean;
    proofTestingInterval: boolean;
    proofTestingCoverage: boolean;
    votingMooNValueM: boolean;
    votingMooNValueN: boolean;
  };
  votingMooNValue: IMooN;
  isDeviceSheetMatch: boolean;
  displayTag: string;
  tagNames: string[];
  proofTestingInterval: number;
  voting: string;
  diagnosticLevel: string;
  dih: number;
  overhaulInterval: number;
  proofTestingCoverageDisplay: string;
  userSpecifiedProofTestingCoverage: boolean;
  votingErrorText?: string;
}

const getLastIndexOfNullOfWhitespace = (arr: string[]) => {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] === null || arr[i].trim() === "") {
      return i;
    }
  }
  return -1;
}

export class DeviceModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const proofTestingCoverage = props.dm_dataSheet == null
      ? this.props.device.ptProtectiveFunction.proofTestingCoverage
      : 1
    const proofTestingCoverageDisplay = (
      proofTestingCoverage * 100
    ).toString();

    this.isBoundDevice = this.props.device != null;

    if (this.isBoundDevice) {
      let voting: string;
      let votingMooNValue: IMooN;

      const dbVoting = this.props.device.ptProtectiveFunction.voting || "1oo1";
      if (isMooNVotingType(dbVoting)) {
        voting = "MooN";
        votingMooNValue = parseMooNVoting(dbVoting);
      } else {
        voting = dbVoting;
        votingMooNValue = parseVoting(dbVoting);
      }

      let deviceTags = this.props.device.ptProtectiveFunction.deviceTags;
      if (votingMooNValue.N > 1 && deviceTags.length === 0) {
        // Old device configurations that aren't 1oo1 wont have the additional device tags
        for (let i = 0; i < votingMooNValue.N; i++) {
          deviceTags.push("");
        }
      }

      this.state = {
        isAllowSubmit: true,
        formValidationFields: {
          diagnosticInterval: true,
          overhaulInterval: true,
          proofTestingInterval: true,
          proofTestingCoverage: true,
          votingMooNValueM: true,
          votingMooNValueN: true
        },
        votingMooNValue: votingMooNValue,
        isDeviceSheetMatch: true,
        displayTag: this.props.device.ptProtectiveFunction.tageName,
        tagNames: deviceTags,
        proofTestingInterval: this.props.device.ptProtectiveFunction.proofTestingInterval,
        voting: voting,
        diagnosticLevel: this.props.device.ptProtectiveFunction.diagnosticLevel || "NO DC",
        dih: this.props.device.ptProtectiveFunction.dih,
        overhaulInterval: this.props.device.ptProtectiveFunction.overhaulInterval,
        proofTestingCoverageDisplay: proofTestingCoverageDisplay,
        userSpecifiedProofTestingCoverage: proofTestingCoverageDisplay !== "100",
      };
    } else {
      this.state = {
        isAllowSubmit: true,
        formValidationFields: {
          diagnosticInterval: true,
          overhaulInterval: true,
          proofTestingInterval: true,
          proofTestingCoverage: true,
          votingMooNValueM: true,
          votingMooNValueN: true
        },
        votingMooNValue: {
          M: 1,
          N: 1
        },
        isDeviceSheetMatch: true,
        displayTag: "",
        tagNames: [],
        proofTestingInterval: 0,
        voting: "1oo1",
        diagnosticLevel: "NO DC",
        dih: 0,
        overhaulInterval: 20,
        proofTestingCoverageDisplay,
        userSpecifiedProofTestingCoverage: proofTestingCoverageDisplay !== "100",
      }
    }
    this.handleRestoreDeviceSheet = this.handleRestoreDeviceSheet.bind(this);

    this.modalTitle = this.isBoundDevice ? "Device" : "Device Sheet";
    this.deviceType = this.isBoundDevice ? this.props.dataSheetInfo.deviceDescription : this.props.dm_dataSheet.deviceDescription;
    this.meanTimeToRepair = this.isBoundDevice ? this.props.device.pmProtectiveFunction.mtrh : this.props.dm_dataSheet.mtrh;
    this.fdfrpy = this.isBoundDevice ? this.props.dataSheetInfo.fdfrpy : this.props.dm_dataSheet.fdfrpy;
    this.fsfrpy = this.isBoundDevice ? this.props.dataSheetInfo.fsfrpy : this.props.dm_dataSheet.fsfrpy;
    this.xccfdm = this.isBoundDevice ? this.props.dataSheetInfo.xccfdm : this.props.dm_dataSheet.xccfdm;
    this.xccftm = this.isBoundDevice ? this.props.dataSheetInfo.xccftm : this.props.dm_dataSheet.xccftm;
    this.xdcsm = this.isBoundDevice ? this.props.dataSheetInfo.xdcsm : this.props.dm_dataSheet.xdcsm;
    this.xdcdm = this.isBoundDevice ? this.props.dataSheetInfo.xdcdm : this.props.dm_dataSheet.xdcdm;
    this.xdctm = this.isBoundDevice ? this.props.dataSheetInfo.xdctm : this.props.dm_dataSheet.xdctm;
  }

  private isBoundDevice: boolean;
  private modalTitle: string;
  private deviceType: string;
  private meanTimeToRepair: number;
  private fdfrpy: number;
  private fsfrpy: number;
  private xccfdm: number;
  private xccftm: number;
  private xdcsm: number;
  private xdcdm: number;
  private xdctm: number;
  private isMooNAvailable = this.props.device?.ptProtectiveFunction.ptType === PtType.InputDevice;

  deviceId = () => {
    return this.props.device
      ? this.props.device.ptProtectiveFunction.deviceId
      : this.props.dm_dataSheet.deviceId
  }

  individualTagsModalState = (isVisible: boolean) => {
    if (isVisible)
      this.setState({ shownModal: "individualTagsModal" });
    else
      this.setState({ shownModal: null });
  }

  updateDeviceTagsArray = (tagsArray: string[]) => {
    this.setState({ tagNames: tagsArray });
  }

  handleTagNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTagName = e.target.value;
    this.setState({ displayTag: newTagName });
  }

  handleVotingDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let newMooN: IMooN;
    let newVoting = e.target.value;
    if (newVoting === "MooN") {
      newMooN = { M: 1, N: 4 };
    } else if (newVoting === "H-PAT") {
      newMooN = { M: 1, N: 1 };
    } else {
      newMooN = parseVoting(newVoting);
    }
    this.handleMooNVotingChange(newVoting, newMooN);
  }

  handleMNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVoting = "MooN";
    const name = e.target.name;
    const newValue = parseInt(e.target.value);

    if (name === "M" && newValue > this.state.votingMooNValue.N) {
      return;
    }
    if (name === "N" && (newValue < 1 || newValue < this.state.votingMooNValue.M)) {
      return;
    }

    const newMooN = { ...this.state.votingMooNValue };
    newMooN[name] = newValue;
    this.handleMooNVotingChange(newVoting, newMooN);
  }

  correctOldN = (deviceTags: string[], nominalN: number) => {
    if (nominalN === 1) {
      return [];
    }
    if (deviceTags.length === nominalN) {
      return deviceTags;
    }
    if (deviceTags.length < nominalN) {
      const deviceTagsCopy = [...deviceTags];
      const diff = nominalN - deviceTags.length;
      for (let i = 0; i < diff; i++) {
        deviceTagsCopy.push("");
      }
      return deviceTagsCopy;
    }
    if (deviceTags.length > nominalN) {
      // remove the last empty string, until none are left, then remove the last string
      let deviceTagsCopy = [...deviceTags];
      const diff2 = deviceTags.length - nominalN;
      for (let i = 0; i < diff2; i++) {
        const index = getLastIndexOfNullOfWhitespace(deviceTagsCopy);
        if (index >= 0) {
          deviceTagsCopy.splice(index, 1)
        }
        else {
          deviceTagsCopy = deviceTagsCopy.slice(-1);
        }
      }
      return deviceTagsCopy;
    }
  }
  //{ voting: string, votingMooNValue: IMooN, tagNames: string[] }
  calculateNextVotingState = (newVoting: string, newMooN: IMooN) => {
    const oldMooN = this.state.votingMooNValue;
    const deviceTags = [...this.state.tagNames];
    const nominalOldN = oldMooN.N;
    const getChange = (oldN: number) => {
      if (oldN < newMooN.N) {
        return "increase";
      }
      if (oldN === newMooN.N) {
        return "same";
      }
      return "decrease";
    }
    const correctedDeviceTags = this.correctOldN(deviceTags, nominalOldN);

    const change = getChange(nominalOldN);

    if (change === "same") {
      return {
        voting: newVoting,
        votingMooNValue: newMooN,
        tagNames: correctedDeviceTags
      };
    }
    else if (change === "increase") {
      const diff = newMooN.N - correctedDeviceTags.length;
      for (let i = 0; i < diff; i++) {
        correctedDeviceTags.push("");
      }
      return {
        voting: newVoting,
        votingMooNValue: newMooN,
        tagNames: correctedDeviceTags
      };
    }
    else if (newMooN.N === 1 ) { // decrease to 1
      const nonEmptyTags = correctedDeviceTags.filter(v => v !== null && v.trim().length > 0);
      if (nonEmptyTags.length === 0) {
       return {
          voting: newVoting,
          votingMooNValue: newMooN,
          tagNames: []
        };
      }
      else {
        return `To change to ${newVoting} you must have all empty device tags.`;
      }
    }
    else { // decrease to something besides 1
      const diff2 = nominalOldN - newMooN.N;

      for (let i = 0; i < diff2; i++) {
        const lastEmptyIndex = getLastIndexOfNullOfWhitespace(correctedDeviceTags)
        if (lastEmptyIndex >= 0) {
          correctedDeviceTags.splice(lastEmptyIndex, 1)
        }
        else {
          return `To change to ${newVoting === "MooN" ? `${newMooN.M}oo${newMooN.N}` : newVoting} you must have ${diff2} empty device tag(s).`;
        }
      }
      return {
        voting: newVoting,
        votingMooNValue: newMooN,
        tagNames: correctedDeviceTags
      };
    }
  } 

  handleMooNVotingChange = (newVoting: string, newMooN: IMooN) => {
    const votingChange = this.calculateNextVotingState(newVoting, newMooN);
    if (typeof votingChange === "string") {
      this.setState({ votingErrorText: votingChange });
    }
    else {
      this.setState({ ...votingChange, votingErrorText: undefined });
    }    
  }

  handleChange = (e: React.ChangeEvent<any>) => {
    const state = { ...this.state };
    state[e.target.name] = e.target.value;
    this.setState(state, this.validateForm);
  };

  diagnosticIntervalChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDih = parseFloat(e.target.value);
    if (newDih >= 0) {
      const state = { ...this.state };
      state.dih = newDih;
      this.setState(state, this.validateForm);
    }
  }

  overhaulIntervalChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOi = parseFloat(e.target.value);
    if (newOi >= 0) {
      const state = { ...this.state };
      state.overhaulInterval = newOi;
      this.setState(state, this.validateForm);
    }
  }

  proofTestingIntervalChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTi = parseFloat(e.target.value);
    if (newTi >= 0) {
      const state = { ...this.state };
      state.proofTestingInterval = newTi;
      this.setState(state, this.validateForm);
    }
  }

  proofTestingCoverageChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percent = e.target.value;
    this.setState({ proofTestingCoverageDisplay: percent }, this.validateForm);
  };

  userSpecifiedClicked(e: any) {
    if (this.props.readOnly) { return; }

    const checked = e.target.checked as boolean;
    let proofTestCoverage = this.state.proofTestingCoverageDisplay;
    if (checked === false) {
      proofTestCoverage = "100";
    }

    this.setState({
      userSpecifiedProofTestingCoverage: checked,
      proofTestingCoverageDisplay: proofTestCoverage
    }, this.validateForm);
  }

  private handleSubmit() {

    const device = { ...this.props.device.ptProtectiveFunction };
    const pm = { ...this.props.device.pmProtectiveFunction };

    device.tageName = this.state.displayTag;
    device.deviceTags = this.state.tagNames;
    device.proofTestingInterval = this.state.proofTestingInterval;
    device.voting = this.state.voting === "MooN"
      ? `MooN${this.state.votingMooNValue.M}oo${this.state.votingMooNValue.N}`
      : this.state.voting;
    device.diagnosticLevel = this.state.diagnosticLevel;
    pm.dih = this.state.dih ?? 0;
    device.overhaulInterval = this.state.overhaulInterval;
    device.proofTestingCoverage = parseFloat(this.state.proofTestingCoverageDisplay);

    this.props.onDeviceUpdateClick(device, pm).then(() => this.props.closeModal());
  }

  private handleRestoreDeviceSheet() {
    this.props.onDeviceSheetRestoreClick(this.props.device.ptProtectiveFunction.protectiveFunctionID).then(() => this.props.closeModal());
  }

  componentDidMount() {
    this.validateForm();
    if (this.props.masterPoolMismatchItems && this.props.masterPoolMismatchItems.find(x => x.protectiveFunctionId == this.props.device.ptProtectiveFunction.protectiveFunctionID)) {
      this.setState({ isDeviceSheetMatch: false })
    }
  }

  validateForm = () => {
    let isAllowSubmit = true;
    const formValidationFields = this.state.formValidationFields;

    const proofTestingCoverage = parseFloat(this.state.proofTestingCoverageDisplay);

    // We don't need to validate when opening a datasheet
    if (!this.isBoundDevice) {
      return;
    }

    if ((this.state.diagnosticLevel !== null && this.state.diagnosticLevel !== "NO DC") && (this.state.dih === 0 || this.state.dih === null)) {
      isAllowSubmit = false;
      formValidationFields.diagnosticInterval = false;
    } else {
      formValidationFields.diagnosticInterval = true;
    }

    if (proofTestingCoverage < 100 && this.state.overhaulInterval === 0) {
      isAllowSubmit = false;
      formValidationFields.overhaulInterval = false;
    } else {
      formValidationFields.overhaulInterval = true;
    }

    if (proofTestingCoverage < 0 || proofTestingCoverage > 100) {
      isAllowSubmit = false;
      formValidationFields.proofTestingCoverage = false;
    } else {
      formValidationFields.proofTestingCoverage = true;
    }

    if (this.state.proofTestingInterval <= 0) {
      isAllowSubmit = false;
      formValidationFields.proofTestingInterval = false;
    } else {
      formValidationFields.proofTestingInterval = true;
    }

    //if (isMooN) {
    //  if ((!MooNCCF || MooNCCF == 0) && (votingMooNValue.M != votingMooNValue.N)) {
    //    isAllowSubmit = false;
    //    formValidationFields.ccfMooNValue = false;
    //  } else {
    //    formValidationFields.ccfMooNValue = true;
    //  }

    //  if (votingMooNValue.M == 0 || votingMooNValue.N == 0) {
    //    if (votingMooNValue.M == 0) {
    //      isAllowSubmit = false;
    //      formValidationFields.votingMooNValueM = false;
    //    } else {
    //      formValidationFields.votingMooNValueM = true;
    //    }
    //    if (votingMooNValue.N == 0) {
    //      isAllowSubmit = false;
    //      formValidationFields.votingMooNValueN = false;
    //    } else {
    //      formValidationFields.votingMooNValueN = true;
    //    }
    //  } else {
    //    if (votingMooNValue.M > votingMooNValue.N) {
    //      isAllowSubmit = false;
    //      formValidationFields.votingMooNValueM = false;
    //    } else {
    //      formValidationFields.votingMooNValueM = true;
    //      formValidationFields.votingMooNValueN = true;
    //    }
    //  }
    //}

    this.setState({
      isAllowSubmit: isAllowSubmit,
      formValidationFields: formValidationFields
    });
  }


  render() {
    //const device = this.state.device;
    //const devicePM = this.state.devicePM;
    const dataSheetInfo = this.props.dataSheetInfo;
    const mooNCCF = this.props.device?.ptProtectiveFunction.mooNCCF || 0;

    //const vm = {
    //  title: isBoundDevice ? "Device" : "Device Sheet",
    //  deviceId: isBoundDevice ? device.deviceId : this.props.dm_dataSheet.deviceId,
    //  deviceType: isBoundDevice ? dataSheetInfo.deviceDescription : this.props.dm_dataSheet.deviceDescription,
    //  voting: "1oo1",
    //  tagName: isBoundDevice ? device.tageName : "",
    //  proofTestingInterval: isBoundDevice ? device.proofTestingInterval : 0,
    //  diagnosticLevel: isBoundDevice ? device.diagnosticLevel : "NO DC",
    //  meanTimeToRepairHrs: isBoundDevice ? dataSheetInfo.mtrh : this.props.dm_dataSheet.mtrh,
    //  dih: isBoundDevice ? devicePM.dih : this.props.dm_dataSheet.dih,
    //  overhaulInterval: isBoundDevice ? device.overhaulInterval : 20,
    //};

    // Check deviceId and lock voting to "1oo1" if lockedVotingDevices Array includes the deviceId

    const formValidationFields = this.state.formValidationFields;

    return (
      <React.Fragment>
        <SilModal
          closeModal={this.props.closeModal}
          title={this.modalTitle}
        >
          <table>
            <tbody>
              <tr>
                <td>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <label
                            htmlFor="txt_tbldeviceid"
                            style={{ fontWeight: 1000 }}
                          >
                            Device ID:
                        </label>
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <input
                            name="txt_tbldeviceid"
                            defaultValue={this.deviceId()}
                            type="text"
                            readOnly
                            id="txt_tbldeviceid"
                            style={{
                              borderStyle: "None",
                              height: "25px",
                              width: "130px",
                              borderBottom: "1px solid #000"
                            }}
                          />
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <label
                            htmlFor="txt_tbldevicetype"
                            style={{ fontWeight: 1000 }}
                          >
                            Device Type:
                        </label>
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <textarea
                            name="txt_tbldevicetype"
                            rows={2}
                            cols={20}
                            readOnly
                            id="txt_tbldevicetype"
                            style={{
                              borderStyle: "None",
                              height: "25px",
                              width: "550px",
                              borderBottom: "1px solid #000"
                            }}
                            value={this.deviceType || ""}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              <tr>
                <td>
                  <div style={{ width: "870px", paddingTop: "10px" }}>
                    <div
                      style={{
                        float: "left",
                        width: "400px",
                        display: "inline-block"
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#90b0f4",
                          paddingBottom: "10px"
                        }}
                      >
                        <label
                          style={{
                            fontSize: "medium",
                            fontWeight: 200,
                            paddingLeft: "10px"
                          }}
                        >
                          Configurations:
                      </label>
                        <table>
                          <tbody>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblDeviceTagDescription"
                                  style={{ fontSize: "small", fontWeight: 200, verticalAlign: "middle", marginBottom: 0 }}
                                >
                                  Display Tag for Device(s):
                              </label>
                                <button
                                  className="device-individual-tags"
                                  disabled={this.state.votingMooNValue.N === 1}
                                  onClick={() => this.individualTagsModalState(true)}
                                >
                                  Individual Tags
                              </button>
                              </td>
                              <td style={{ paddingLeft: "58px" }}>
                                <input
                                  name="tageName"
                                  value={this.state.displayTag}
                                  type="text"
                                  maxLength={25}
                                  id="txt_tblDeviceTagDescription"
                                  style={{ height: "20px", width: "100px" }}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => this.handleTagNameChange(e)}
                                  readOnly={!this.isBoundDevice}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblProof_TInterval"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Proof Testing Interval (yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "58px" }}>
                                <input
                                  name="proofTestingInterval"
                                  value={this.state.proofTestingInterval}
                                  type="number"
                                  id="txt_tblProof_TInterval"
                                  style={{ height: "20px", width: "100px" }}
                                  min="0"
                                  step=".25"
                                  onChange={(e) => this.proofTestingIntervalChanged(e)}
                                  readOnly={!this.isBoundDevice}
                                  className={`${!formValidationFields.proofTestingInterval ? "input-error-state" : ""}`}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="ddldevicevoting"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Voting:
                              </label>
                              </td>
                              <td style={{ paddingLeft: "58px" }}>
                                <select
                                  name="voting"
                                  value={this.state.voting}
                                  id="ddldevicevoting"
                                  style={{ height: "20px", width: "100px" }}
                                  onChange={(e) => this.handleVotingDropdownChange(e)}
                                  disabled={!this.isBoundDevice || lockedVotingDevices.indexOf(this.deviceId()) > -1}
                                >
                                  <option value="1oo1">1oo1</option>
                                  <option value="1oo2">1oo2</option>
                                  <option value="2oo2">2oo2</option>
                                  <option value="1oo3">1oo3</option>
                                  <option value="2oo3">2oo3</option>
                                  <option value="3oo3">3oo3</option>
                                  <option value="H-PAT">H-PAT</option>
                                  {this.isMooNAvailable && (<option value="MooN">MooN</option>)}
                                </select>
                              </td>
                            </tr>
                            {this.state.voting === "MooN" ?
                              <React.Fragment>
                                <tr>
                                  <td style={{ paddingLeft: "10px" }}>
                                    <label
                                      htmlFor="ddldevicevotingm"
                                      style={{ fontSize: "small", fontWeight: 200 }}
                                    >
                                      Specify M:
                                  </label>
                                  </td>
                                  <td style={{ paddingLeft: "58px" }}>
                                    <input
                                      name="M"
                                      value={this.state.votingMooNValue.M}
                                      type="number"
                                      min="1"
                                      id="ddldevicevotingm"
                                      style={{ height: "20px", width: "100px" }}
                                      onChange={(e) => this.handleMNChange(e)}
                                      readOnly={!this.isBoundDevice}
                                      className={`${!formValidationFields.votingMooNValueM ? "input-error-state" : ""}`}
                                    />
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ paddingLeft: "10px" }}>
                                    <label
                                      htmlFor="ddldevicevotingn"
                                      style={{ fontSize: "small", fontWeight: 200 }}
                                    >
                                      Specify N:
                                  </label>
                                  </td>
                                  <td style={{ paddingLeft: "58px" }}>
                                    <input
                                      name="N"
                                      value={this.state.votingMooNValue.N}
                                      type="number"
                                      min="1"
                                      id="ddldevicevotingn"
                                      style={{ height: "20px", width: "100px" }}
                                      onChange={(e) => this.handleMNChange(e)}
                                      readOnly={!this.isBoundDevice}
                                      className={`${!formValidationFields.votingMooNValueN ? "input-error-state" : ""}`}
                                    />
                                  </td>
                                </tr>
                              </React.Fragment>
                              : null}
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="ddldiagnosticlevel"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Subsystem Diagnostic Level:
                              </label>{" "}
                                <span
                                  className="sil-tooltip"
                                  title="Select DC level 1, 2, or 3 if diagnostics will be used as a vote to trigger safe action or to initiate a timely compensating measure and device has diagnostic coverage > 0 for that level.  Select the level based on the number of safety function devices that are part of the diagnostics.  See User Guide for examples."
                                >
                                  ?
                              </span>
                              </td>
                              <td style={{ paddingLeft: "58px" }}>
                                <select
                                  name="diagnosticLevel"
                                  value={this.state.diagnosticLevel}
                                  id="ddldiagnosticlevel"
                                  style={{ height: "20px", width: "100px" }}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLSelectElement>
                                  ) => this.handleChange(e)}
                                  disabled={!this.isBoundDevice}
                                >
                                  <option value="NO DC">NO DC</option>
                                  <option value="DC1" disabled={this.props.device?.pmProtectiveFunction.dcsm <= 0}>DC1</option>
                                  <option value="DC2" disabled={this.props.device?.pmProtectiveFunction.dcdm <= 0}>DC2</option>
                                  <option value="DC3" disabled={this.props.device?.pmProtectiveFunction.dctm <= 0}>DC3</option>
                                </select>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <label style={{ color: "red" }}> {this.state.votingErrorText}</label>
                      <br className="spacer" />
                      <div
                        style={{
                          backgroundColor: "#e4f9ca",
                          paddingBottom: "10px"
                        }}
                      >
                        <label
                          style={{
                            fontSize: "medium",
                            fontWeight: 200,
                            paddingLeft: "10px"
                          }}
                        >
                          Maintenance:
                      </label>
                        <table>
                          <tbody>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblMeanTimetoRepair"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Mean Time to Repair (hr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "90px" }}>
                                <input
                                  name="txt_tblMeanTimetoRepair"
                                  defaultValue={this.meanTimeToRepair}
                                  type="text"
                                  readOnly
                                  id="txt_tblMeanTimetoRepair"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#e4f9ca"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblDiagnostic_Interval"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Diagnostic Interval (hr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "90px" }}>
                                <input
                                  name="dih"
                                  value={this.state.dih}
                                  step="0.25"
                                  type="number"
                                  id="txt_tblDiagnostic_Interval"
                                  style={{ height: "20px", width: "100px" }}
                                  onChange={(e) => this.diagnosticIntervalChanged(e)}
                                  readOnly={!this.isBoundDevice}
                                  className={`${!formValidationFields.diagnosticInterval ? "input-error-state" : ""}`}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblOverhaulInterval"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Overhaul Interval (yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "90px" }}>
                                <input
                                  name="overhaulInterval"
                                  value={this.state.overhaulInterval}
                                  type="number"
                                  id="txt_tblOverhaulInterval"
                                  style={{ height: "20px", width: "100px" }}
                                  onChange={(e) => this.overhaulIntervalChanged(e)}
                                  readOnly={!this.isBoundDevice}
                                  className={`${!formValidationFields.overhaulInterval ? "input-error-state" : ""}`}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblProof_TCoverage"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Proof Testing Coverage (%):
                              </label>{" "}
                                <span
                                  className="sil-tooltip"
                                  title="Use this field when the proof test procedure cannot detect all the dangerous failure modes of the installed device.  The % proof test coverage (PTC) is the percentage of the failure rate that the procedure can detect. The Overhaul Interval is the interval at which all remaining failure modes will be addressed (e.g., a 100% effective test, replacement, or rebuilding the device “as good as new”). See User Guide for example.  If a PTC < 100% is entered, the overhaul interval must be > 0."
                                >
                                  ?
                              </span>
                              </td>
                              <td style={{ paddingLeft: "90px" }}>
                                <input
                                  name="proofTestingCoverage"
                                  type="number"
                                  value={this.state.proofTestingCoverageDisplay}
                                  id="txt_tblProof_TCoverage"
                                  style={{ height: "20px", width: "100px" }}
                                  disabled={
                                    !this.state.userSpecifiedProofTestingCoverage
                                  }
                                  onChange={this.proofTestingCoverageChanged}
                                  className={`${!formValidationFields.proofTestingCoverage ? "input-error-state" : ""}`}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "40px" }}>
                                <label
                                  htmlFor="cb_prooftesing_coverage"
                                  style={{ fontSize: "small", fontWeight: 100 }}
                                >
                                  User Specified
                              </label>
                              </td>
                              <td style={{ paddingLeft: "90px" }}>
                                <input
                                  id="cb_prooftesing_coverage"
                                  type="checkbox"
                                  name="cb_prooftesing_coverage"
                                  checked={
                                    this.state.userSpecifiedProofTestingCoverage
                                  }
                                  onChange={e => this.userSpecifiedClicked(e)}
                                  disabled={!this.isBoundDevice}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      style={{
                        float: "right",
                        display: "inline-block",
                        width: "450px"
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#eee5f9",
                          paddingBottom: "10px"
                        }}
                      >
                        <label
                          style={{
                            fontSize: "medium",
                            fontWeight: 200,
                            paddingLeft: "10px"
                          }}
                        >
                          Properties:
                      </label>
                        <table>
                          <tbody>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblFailure_DFR"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Fail Dangerous Failure Rate (1/yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblFailure_DFR"
                                  defaultValue={formatNumberNotation(this.fdfrpy)}
                                  type="text"
                                  readOnly
                                  id="txt_tblFailure_DFR"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblFailure_SFR"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Fail Safe Failure Rate (1/yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblFailure_SFR"
                                  defaultValue={formatNumberNotation(this.fsfrpy)}
                                  type="text"
                                  readOnly
                                  id="txt_tblFailure_SFR"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblCCF_Dual"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Common Cause Factor CCF Dual (%):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblCCF_Dual"
                                  defaultValue={this.xccfdm}
                                  type="text"
                                  readOnly
                                  id="txt_tblCCF_Dual"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblCCF_Tripe"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Common Cause Factor CCF Triple (%):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblCCF_Tripe"
                                  defaultValue={this.xccftm}
                                  type="text"
                                  readOnly
                                  id="txt_tblCCF_Tripe"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblDiagnostic_CS"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Diagnostic Coverage Simplex DC1 (1/yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblDiagnostic_CS"
                                  defaultValue={this.xdcsm}
                                  type="text"
                                  readOnly
                                  id="txt_tblDiagnostic_CS"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblDiagnostic_CD"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Diagnostic Coverage Dual DC2 (1/yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblDiagnostic_CD"
                                  defaultValue={this.xdcdm}
                                  type="text"
                                  readOnly
                                  id="txt_tblDiagnostic_CD"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tblDiagnostic_CT"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  Diagnostic Coverage Triple DC3 (1/yr):
                              </label>
                              </td>
                              <td style={{ paddingLeft: "80px" }}>
                                <input
                                  name="txt_tblDiagnostic_CT"
                                  defaultValue={this.xdctm}
                                  type="text"
                                  readOnly
                                  id="txt_tblDiagnostic_CT"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#eee5f9"
                                  }}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <br className="spacer" />
                      <div style={{ paddingBottom: "10px" }}>
                        <table
                          style={{
                            background: "#6b932a",
                            borderRadius: "10px",
                            MozBorderRadius: "10px",
                            WebkitBorderRadius: "10px",
                            width: "240px",
                            height: "70px"
                          }}
                        >
                          <tbody>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tbldevicePFDavg"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  PFDavg:
                              </label>
                              </td>
                              <td style={{ paddingLeft: "50px" }}>
                                <input
                                  name="txt_tbldevicePFDavg"
                                  defaultValue={this.props.device?.ptProtectiveFunction.nonCcfPfdAverage.display || ""}
                                  type="text"
                                  id="txt_tbldevicePFDavg"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#6b932a"
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingLeft: "10px" }}>
                                <label
                                  htmlFor="txt_tbldeviceSTR"
                                  style={{ fontSize: "small", fontWeight: 200 }}
                                >
                                  STR:
                              </label>
                              </td>
                              <td style={{ paddingLeft: "50px" }}>
                                <input
                                  name="txt_tbldeviceSTR"
                                  defaultValue={formatNumberNotation(this.props.dataSheetInfo?.stR1Year)}
                                  type="text"
                                  id="txt_tbldeviceSTR"
                                  style={{
                                    borderStyle: "None",
                                    height: "20px",
                                    width: "100px",
                                    borderBottom: "1px solid #000",
                                    backgroundColor: "#6b932a"
                                  }}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ paddingTop: "10px" }}>
                          <label
                            htmlFor="txt_tblnote"
                            style={{ fontSize: "medium", fontWeight: "bold" }}
                          >
                            Note:
                        </label>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <textarea
                            name="txt_tblnote"
                            rows={2}
                            cols={20}
                            id="txt_tblnote"
                            style={{ height: "100px", width: "870px" }}
                            defaultValue={this.props.dataSheetInfo?.dnts || this.props.dm_dataSheet?.dnts}
                            readOnly={!this.isBoundDevice}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              <tr>
                <td>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <label
                            htmlFor="txt_tbldevicedatasource"
                            style={{ fontWeight: 1000 }}
                          >
                            Data Source:
                        </label>
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <input
                            name="txt_tbldevicedatasource"
                            defaultValue={this.isBoundDevice ? dataSheetInfo.source : this.props.dm_dataSheet.source}
                            type="text"
                            readOnly
                            id="txt_tbldevicedatasource"
                            style={{
                              borderStyle: "None",
                              width: "100px",
                              borderBottom: "1px solid #000"
                            }}
                          />
                        </td>
                        {this.isBoundDevice &&
                          <td style={{ paddingLeft: "180px", paddingTop: "10px" }}>
                            <input
                              disabled={!this.state.isAllowSubmit || this.props.readOnly}
                              className={`edit-button ${!this.state.isAllowSubmit || this.props.readOnly ? 'disabled' : ""}`}
                              type="submit"
                              name="Update_tbldevice"
                              value="Update"
                              id="Update_tbldevice"
                              onClick={() => this.handleSubmit()}
                            />
                            {!this.state.isDeviceSheetMatch && (
                              <input
                              className="edit-button"
                              disabled={this.props.readOnly}
                                type="submit"
                                name="Restore_devicesheet_tbldevice"
                                value="Upgrade device sheet"
                                id="Restore_devicesheet_tbldevice"
                                onClick={this.handleRestoreDeviceSheet}
                              />
                            )}
                          </td>
                        }
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </SilModal>
        {this.state.shownModal === "individualTagsModal" ? (
          <IndividualTagsModal
            onModalStateChange={this.individualTagsModalState}
            displayTagName={this.state.displayTag}
            tagNames={this.state.tagNames}
            onSubmit={this.updateDeviceTagsArray}
          />
        ) : null}
      </React.Fragment>
    );
  }
}
